"use client";

import { useEffect, useState } from "react";
import { Comedian, getPublishedComediansCache } from "@/data/comedians";
import { SpecialRow } from "@/data/specials";
import { getApiClient } from "@punchup/utils/apiRegistry";
import dayjs from "@punchup/utils/dayjs";

type DiscoverDataOptions = {
  comedianLimit?: number;
  specialLimit?: number;
};

type DiscoverData = {
  allComediansMap: Map<string, Comedian>;
  rankedComedians: Comedian[];
  trendingSpecials: SpecialRow[];
  isPersonalized: boolean;
  loaded: boolean;
};

/**
 * Shared hook that powers both the Discover page and Search overlay default state.
 *
 * Fetches comedian rankings from /api/v2/public/discover/comedians (momentum or ALS)
 * and trending content from /api/v2/public/discover/content (trending_specials).
 */
export function useDiscoverData({
  comedianLimit = 50,
  specialLimit = 50,
}: DiscoverDataOptions = {}): DiscoverData {
  const [allComediansMap, setAllComediansMap] = useState<Map<string, Comedian>>(
    new Map()
  );
  const [rankedComedians, setRankedComedians] = useState<Comedian[]>([]);
  const [trendingSpecials, setTrendingSpecials] = useState<SpecialRow[]>([]);
  const [isPersonalized, setIsPersonalized] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;

    const load = async () => {
      try {
        // Fetch all three in parallel
        const [comedians, comedianData, contentData] = await Promise.all([
          getPublishedComediansCache(),
          getApiClient().get<{
            data: { comedian_ids: string[]; is_personalized: boolean };
          }>(`/api/v2/public/discover/comedians?limit=${comedianLimit}`),
          getApiClient().get<{
            data: { special_ids: string[]; specials: SpecialRow[] };
          }>(`/api/v2/public/discover/content?limit=${specialLimit}`),
        ]);

        const comedianMap = new Map(comedians.map((c) => [c.id, c]));
        setAllComediansMap(comedianMap);

        // Process comedian rankings
        if (comedianData?.data) {
          const { comedian_ids, is_personalized } = comedianData.data;
          const ranked = (comedian_ids as string[])
            .map((id: string) => comedianMap.get(id))
            .filter(
              (c): c is Comedian =>
                c !== undefined &&
                c.published_at !== null &&
                c.is_external_preview !== true
            );
          setRankedComedians(ranked);
          setIsPersonalized(is_personalized);
        } else {
          // Fallback: sort by published_at
          const fallback = Array.from(comedianMap.values())
            .filter(
              (c) => c.published_at !== null && c.is_external_preview !== true
            )
            .sort(
              (a, b) =>
                dayjs(b.published_at!).valueOf() -
                dayjs(a.published_at!).valueOf()
            );
          setRankedComedians(fallback);
        }

        // Process trending content
        if (contentData?.data) {
          const { specials, special_ids } = contentData.data;
          if (specials && special_ids) {
            const specialMap = new Map(
              (specials as SpecialRow[]).map((s) => [s.id, s])
            );
            const ordered = (special_ids as string[])
              .map((id: string) => specialMap.get(id))
              .filter((s): s is SpecialRow => s !== undefined);
            setTrendingSpecials(ordered);
          }
        }

        setLoaded(true);
      } catch (error) {
        console.error("Error loading discover data:", error);
        setLoaded(true);
      }
    };

    load();
  }, [loaded, comedianLimit, specialLimit]);

  return {
    allComediansMap,
    rankedComedians,
    trendingSpecials,
    isPersonalized,
    loaded,
  };
}
